/**
 * Create / edit a material gate pass (DRAFT only).
 *
 * Returnable vs Non-Returnable is a property of the document, not an instruction
 * to the ledger — this screen records what is crossing the gate and why. The one
 * field that has to be got right is the purpose: everything downstream (the gate
 * register, the audit trail, the printed challan) reads it, which is why OTHER
 * cannot be saved without saying what "other" was.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Alert, CircularProgress, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useHistory, useParams, useRouteMatch, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import InvButton from '../../components/InvButton';
import { InvTextField, InvSelect } from '../../components/InvField';
import GatePassLineTable, {
  GATE_PASS_LINE_DEFAULT,
  isStoreLine,
  toNumberOrNull,
} from '../../components/stock/GatePassLineTable';
import { createGatePass, getGatePass, updateGatePass, submitGatePass } from '../../api/gatePass';
import { getStores } from '../../api/stockLedger';
import { getVendorReturns, getVendorReturn } from '../../api/vendorReturn';
import { getGoodsDiscards, getGoodsDiscard } from '../../api/goodsDiscard';
import { toInventoryList } from '../../api/inventory-request';
import {
  GATE_PASS_PURPOSES,
  GATE_PASS_PURPOSE_LABELS,
  GATE_PASS_PARTY_TYPES,
  GATE_PASS_PARTY_TYPE_LABELS,
  GATE_PASS_LINE_TYPES,
  GATE_PASS_SOURCE_TYPES,
  GATE_PASS_SOURCE_BY_PURPOSE,
  GATE_PASS_SOURCE_LABELS,
  RETURNABLE_OPTIONS,
  PURPOSE_DEFAULT_RETURNABLE,
  PURPOSE_REMARKS_LABEL,
  PURPOSE_REMARKS_PLACEHOLDER,
} from '../../constants/gatePass';
import {
  GATE_PASS_SOURCE_READY_STATUS,
  sourceDocumentOptions,
  sourceDocumentToLines,
} from '../../utils/gatePassSource';
import { fetchItemCatalog } from '../../utils/itemDisplay';
import { storeDisplayName } from '../../utils/storeEntity';
import { filterStoresByAssignment } from '../../utils/assignedStores';
import useInvRole from '../../hooks/useInvRole';
import { INV_ACTIONS } from '../../utils/inventoryRoles';
import { IPD_COLORS } from '../../theme';

const PURPOSE_OPTIONS = [
  { value: '', label: 'Select purpose *' },
  ...GATE_PASS_PURPOSES.map(v => ({ value: v, label: GATE_PASS_PURPOSE_LABELS[v] })),
];

const PARTY_TYPE_OPTIONS = GATE_PASS_PARTY_TYPES.map(v => ({
  value: v,
  label: GATE_PASS_PARTY_TYPE_LABELS[v],
}));

export default function GatePassForm() {
  const history = useHistory();
  const { id } = useParams();
  const { url } = useRouteMatch();
  const location = useLocation();
  const isEdit = Boolean(id) && location.pathname.endsWith('/edit');
  const listUrl = url.replace(/\/gate-pass\/(new|[^/]+\/edit)$/, '/gate-passes');
  const detailBase = url.replace(/\/(new|[^/]+\/edit)$/, '');

  const { can, myStoreId, myStoreIds } = useInvRole();

  const [stores, setStores] = useState([]);
  const [lines, setLines] = useState([{ ...GATE_PASS_LINE_DEFAULT }]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState(null);
  // The Vendor Return / Goods Discard this pass rides on, when there is one.
  const [sourceDocs, setSourceDocs] = useState([]);
  const [sourceDocId, setSourceDocId] = useState('');
  const [sourceLoading, setSourceLoading] = useState(false);

  const { control, handleSubmit, watch, reset, setValue, formState: { errors } } = useForm({
    defaultValues: {
      direction: 'OUTWARD',
      returnable_flag: 'NON_RETURNABLE',
      purpose: '',
      purpose_remarks: '',
      gate_pass_datetime: dayjs().format('YYYY-MM-DDTHH:mm'),
      store_id: '',
      expected_return_date: '',
      party_type: 'VENDOR',
      party_name: '',
      party_contact: '',
      vehicle_no: '',
      carrier_name: '',
      carrier_contact: '',
      credit_note_number: '',
      credit_note_date: '',
      credit_note_amount: '',
      credit_note_remarks: '',
      remarks: '',
    },
  });

  const returnableFlag = watch('returnable_flag');
  const purpose = watch('purpose');
  // 'Other' is not a purpose until the user says what it is.
  const showPurposeRemarks = purpose === 'OTHER';
  const storeId = watch('store_id');
  const returnable = returnableFlag === 'RETURNABLE';
  const showCreditNote = purpose === 'VENDOR_RETURN' && !returnable;

  /**
   * Which document, if any, this pass is the physical-exit record for.
   *
   * Only a non-returnable pass can have one: a Vendor Return and a Goods Discard
   * have both already disposed of the material, so nothing is coming back. Also
   * create-only — GatePassUpdate does not accept source_document_*, so offering
   * it on an edit would show a control the PUT silently drops.
   */
  const sourceType = !returnable && !isEdit
    ? GATE_PASS_SOURCE_BY_PURPOSE[purpose] || ''
    : '';
  const sourceLinked = Boolean(sourceType && sourceDocId);

  useEffect(() => {
    getStores()
      .then(d => setStores(toInventoryList(d)))
      .catch(() => {});
  }, []);

  /**
   * The pickable source documents for the current purpose.
   *
   * Neither list endpoint takes a store filter, so the whole ready set is
   * fetched once per purpose and narrowed to the selected store client-side by
   * sourceDocumentOptions — which is also why the fetch does not depend on
   * storeId: changing store must not re-hit the network.
   */
  useEffect(() => {
    if (!sourceType) {
      setSourceDocs([]);
      return;
    }
    const status = GATE_PASS_SOURCE_READY_STATUS[sourceType];
    const request = sourceType === GATE_PASS_SOURCE_TYPES.VENDOR_RETURN
      // API: GET /inventory/vendor-returns/?status=CONFIRMED
      ? getVendorReturns({}, { status, limit: 200 })
      // API: GET /inventory/goods-discard/?status=APPROVED
      : getGoodsDiscards({ status, limit: 200 });
    let alive = true;
    request
      .then(d => { if (alive) setSourceDocs(toInventoryList(d)); })
      .catch(() => { if (alive) setSourceDocs([]); });
    return () => { alive = false; };
  }, [sourceType]);

  // Leaving the sourced purposes (or switching to Returnable) drops the link and
  // gives the user an empty, editable table back rather than locked rows from a
  // document this pass no longer refers to.
  useEffect(() => {
    if (sourceType || !sourceDocId) return;
    setSourceDocId('');
    setLines([{ ...GATE_PASS_LINE_DEFAULT }]);
  }, [sourceType, sourceDocId]);

  /**
   * Pull the picked document's lines in, then lock the table.
   *
   * Vendor return lines carry no item_name (grn-batch-service resolves nothing
   * from inventory-master on that read), so the item catalog — one module-wide
   * cached GET — supplies both the name and the material group. A catalog
   * failure is not fatal: the lines still load, just without names.
   */
  async function pickSourceDocument(docId) {
    setSourceDocId(docId);
    if (!docId) {
      setLines([{ ...GATE_PASS_LINE_DEFAULT }]);
      return;
    }
    setSourceLoading(true);
    try {
      const [doc, catalog] = await Promise.all([
        sourceType === GATE_PASS_SOURCE_TYPES.VENDOR_RETURN
          // API: GET /inventory/vendor-returns/{id}/
          ? getVendorReturn(docId)
          // API: GET /inventory/goods-discard/{id}/
          : getGoodsDiscard(docId),
        fetchItemCatalog().catch(() => null),
      ]);
      const describe = itemId => ({ item_name: catalog?.resolveById(itemId) || '' });
      const sourced = sourceDocumentToLines(sourceType, doc, describe);
      if (!sourced.length) {
        toast.error('That document has no lines to carry');
        setSourceDocId('');
        return;
      }
      setLines(sourced);
    } catch (e) {
      toast.error(e.message || 'Failed to load the source document');
      setSourceDocId('');
      setLines([{ ...GATE_PASS_LINE_DEFAULT }]);
    } finally {
      setSourceLoading(false);
    }
  }

  // Store-scoped users raise passes from their own store only. Create path only —
  // the edit path reset()s from the server and must keep the saved store.
  const lockStore = !isEdit && myStoreIds.length === 1
    && stores.some(s => (s.store_id || s.id) === myStoreId);

  useEffect(() => {
    if (lockStore) setValue('store_id', myStoreId);
  }, [lockStore, myStoreId, setValue]);

  const loadExisting = useCallback(async () => {
    if (!isEdit || !id) return;
    setLoading(true);
    setLoadError(null);
    try {
      // API: GET /inventory/gate-pass/{id}/
      const data = await getGatePass(id);
      reset({
        direction: data.direction || 'OUTWARD',
        returnable_flag: data.returnable_flag || 'NON_RETURNABLE',
        purpose: data.purpose || '',
        purpose_remarks: data.purpose_remarks || '',
        gate_pass_datetime: data.gate_pass_datetime
          ? dayjs(data.gate_pass_datetime).format('YYYY-MM-DDTHH:mm')
          : dayjs().format('YYYY-MM-DDTHH:mm'),
        store_id: data.store_id || '',
        expected_return_date: data.expected_return_date || '',
        party_type: data.party_type || 'VENDOR',
        party_name: data.party_name || '',
        party_contact: data.party_contact || '',
        vehicle_no: data.vehicle_no || '',
        carrier_name: data.carrier_name || '',
        carrier_contact: data.carrier_contact || '',
        credit_note_number: data.credit_note_number || '',
        credit_note_date: data.credit_note_date || '',
        credit_note_amount: data.credit_note_amount ?? '',
        credit_note_remarks: data.credit_note_remarks || '',
        remarks: data.remarks || '',
      });
      setLines(
        (data.lines || []).length
          ? data.lines.map(l => ({
            ...GATE_PASS_LINE_DEFAULT,
            // Nothing persists the line's shape — item_id is what distinguishes
            // a stock line from an off-master one, so it is read back out of the
            // document rather than stored twice and risking disagreement.
            line_type: l.item_id ? GATE_PASS_LINE_TYPES.STORE : GATE_PASS_LINE_TYPES.OTHER,
            item_key: l.item_id || '',
            item_id: l.item_id || '',
            item_name: l.item_name || '',
            item_description: l.item_description || '',
            batch_number: l.batch_number || '',
            grn_item_id: l.grn_item_id || '',
            // Seed the batch dropdown from the saved line so re-opening a draft
            // shows the batch it already names. batchRowKey falls back to
            // grn_item_id, so this one synthetic option is selected on render;
            // re-picking the item replaces it with the store's real batch list.
            _row_key: l.grn_item_id || '',
            _batches: l.grn_item_id
              ? [{
                grn_item_id: l.grn_item_id,
                batch_number: l.batch_number || '',
                expiry_date: l.expiry_date || '',
              }]
              : [],
            expiry_date: l.expiry_date || '',
            uom_id: l.uom_id || '',
            serial_number: l.serial_number || '',
            quantity: l.quantity ?? '',
            remarks: l.remarks || '',
          }))
          : [{ ...GATE_PASS_LINE_DEFAULT }],
      );
    } catch (e) {
      setLoadError(e.message || 'Failed to load gate pass');
    } finally {
      setLoading(false);
    }
  }, [isEdit, id, reset]);

  useEffect(() => { loadExisting(); }, [loadExisting]);

  /**
   * Picking a purpose suggests the Returnable side, it never locks it. Repair is
   * a returnable job, scrap is not — opening that toggle on a coin-flip is how
   * the one mistake that silently changes stock behaviour gets made.
   * Suppressed while editing: the saved document already decided.
   */
  function onPurposeChange(value, field) {
    field.onChange(value);
    if (isEdit) return;
    const suggested = PURPOSE_DEFAULT_RETURNABLE[value];
    if (suggested) setValue('returnable_flag', suggested);
  }

  /**
   * Changing store empties the item lines.
   *
   * Every item picker on this form searches ONE store's catalogue, so lines left
   * behind after a switch name stock the new store does not hold — and because
   * a gate pass posts nothing, nothing downstream would ever catch it. The
   * document would simply be wrong, silently.
   *
   * Handled here rather than in an effect on storeId so that reset() on the edit
   * path and the store-scoped user's auto-select — neither of which is a user
   * changing their mind — do not wipe lines that were just loaded.
   */
  function changeStore(event, field) {
    const next = event.target.value;
    if (next !== field.value) {
      const hadWork = lines.some(l => l.item_id || (l.item_description || '').trim());
      setLines([{ ...GATE_PASS_LINE_DEFAULT }]);
      if (hadWork) toast.info('Store changed — item lines cleared');
    }
    field.onChange(event);
  }

  /**
   * A line counts once it has a positive quantity and something that identifies
   * it — an item from the store, or a description for an off-master asset.
   * Which of the two is decided per row, not per document.
   */
  function validLines() {
    return lines.filter((l) => {
      const qty = toNumberOrNull(l.quantity);
      if (qty == null || qty <= 0) return false;
      return isStoreLine(l) ? Boolean(l.item_id) : Boolean((l.item_description || '').trim());
    });
  }

  function buildBody(data) {
    return {
      direction: data.direction,
      returnable_flag: data.returnable_flag,
      purpose: data.purpose,
      // Always sent, empty when the purpose is a named one. An `undefined` here
      // would be dropped from the PUT body, and GatePassService.update only
      // writes fields that are not None — so switching a saved draft from Other
      // to Repair would leave the old "other" text on the document.
      purpose_remarks: showPurposeRemarks ? data.purpose_remarks.trim() : '',
      gate_pass_datetime: data.gate_pass_datetime
        ? dayjs(data.gate_pass_datetime).toISOString()
        : undefined,
      store_id: data.store_id || undefined,
      expected_return_date: returnable ? data.expected_return_date : undefined,
      party_type: data.party_type,
      party_name: data.party_name.trim(),
      party_contact: data.party_contact || undefined,
      vehicle_no: data.vehicle_no || undefined,
      carrier_name: data.carrier_name || undefined,
      carrier_contact: data.carrier_contact || undefined,
      credit_note_number: showCreditNote ? (data.credit_note_number || undefined) : undefined,
      credit_note_date: showCreditNote ? (data.credit_note_date || undefined) : undefined,
      credit_note_amount: showCreditNote && data.credit_note_amount !== ''
        ? Number(data.credit_note_amount)
        : undefined,
      credit_note_remarks: showCreditNote ? (data.credit_note_remarks || undefined) : undefined,
      remarks: data.remarks || undefined,
      // Only ever sent on create — GatePassUpdate has no source_document_* at
      // all, and the pair must travel together or GatePassCreate rejects it.
      source_document_type: sourceLinked ? sourceType : undefined,
      source_document_id: sourceLinked ? sourceDocId : undefined,
      // Each line fills in one half of GatePassItem. GatePassLineCreate refuses
      // a grn_item_id without an item_id ("grn_item_id names a batch of a master
      // item"), so the two shapes stay strictly separate.
      lines: validLines().map(l => (isStoreLine(l)
        ? {
          item_id: l.item_id,
          grn_item_id: l.grn_item_id || undefined,
          batch_number: l.batch_number || undefined,
          expiry_date: l.expiry_date || undefined,
          uom_id: l.uom_id || undefined,
          quantity: Number(l.quantity),
          remarks: l.remarks || undefined,
        }
        : {
          item_description: l.item_description.trim(),
          serial_number: l.serial_number || undefined,
          quantity: Number(l.quantity),
          remarks: l.remarks || undefined,
        })),
    };
  }

  function validate(data) {
    // Mirrors GatePassCreate._check_document_shape — the server refuses this
    // pairing too, and a 422 after the user has filled the whole form is a
    // worse way to learn it.
    if (showPurposeRemarks && !data.purpose_remarks.trim()) {
      toast.error('Describe the purpose — "Other" on its own is not a gate register entry');
      return false;
    }
    const valid = validLines();
    if (!valid.length) {
      toast.error('Add at least one line with an item and quantity');
      return false;
    }
    if (returnable && !data.expected_return_date) {
      toast.error('Expected return date is required on a returnable gate pass');
      return false;
    }
    // Required on both kinds now, not just non-returnable: every item picker on
    // this form searches ONE store's catalogue, so without a store there is
    // nothing to pick from in the first place.
    if (!data.store_id) {
      toast.error('Store is required — it is the store the material is leaving from');
      return false;
    }
    // A batch identifies WHICH stock left, and only a store line has one. An
    // off-master line is identified by its serial number instead, which is
    // optional: the description alone is a legitimate register entry.
    const noBatch = valid.filter(isStoreLine).find(l => !l.grn_item_id);
    if (noBatch) {
      toast.error(`Select a batch for ${noBatch.item_name || 'every item'} — the batch is what identifies which stock left`);
      return false;
    }
    // Quantity is NOT checked against on-hand. A gate pass posts nothing, so it
    // must be able to record what physically left even when the books disagree —
    // refusing the entry would only hide the discrepancy.
    return true;
  }

  async function save(data, thenSubmit) {
    if (!validate(data)) return;
    const body = buildBody(data);
    setSaving(true);
    try {
      let gatePassId = id;
      if (isEdit) {
        // API: PUT /inventory/gate-pass/{id}/
        await updateGatePass(id, body);
      } else {
        // API: POST /inventory/gate-pass/
        const created = await createGatePass(body);
        gatePassId = created?.id;
      }

      if (thenSubmit && gatePassId) {
        // API: POST /inventory/gate-pass/{id}/submit/
        await submitGatePass(gatePassId);
        toast.success('Gate pass sent for approval');
      } else {
        toast.success(isEdit ? 'Gate pass updated' : 'Draft gate pass created');
      }
      history.push(gatePassId ? `${detailBase}/${gatePassId}` : listUrl);
    } catch (e) {
      toast.error(e.message || 'Failed to save gate pass');
    } finally {
      setSaving(false);
    }
  }

  const storeOptions = [
    { value: '', label: 'Select store' },
    ...filterStoresByAssignment(stores, myStoreIds).map(s => ({
      value: s.store_id || s.id,
      label: storeDisplayName(s),
    })),
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress sx={{ color: IPD_COLORS.primary }} />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>
        <InvButton variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => history.push(listUrl)}>
          Back
        </InvButton>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit(d => save(d, false))}>
      <PageHeader
        breadcrumb={['Inventory Management', 'Gate Pass']}
        title={isEdit ? 'Edit Gate Pass' : 'New Gate Pass'}
        description="Material leaving hospital premises"
        actions={(
          <>
            <InvButton variant="outlined" size="small" startIcon={<ArrowBackIcon />} onClick={() => history.push(listUrl)}>
              Back
            </InvButton>
            <InvButton type="submit" variant="outlined" size="small" loading={saving}>
              Save draft
            </InvButton>
            {can(INV_ACTIONS.GATEPASS_SUBMIT) && (
              <InvButton size="small" loading={saving} onClick={handleSubmit(d => save(d, true))}>
                Save &amp; send for approval
              </InvButton>
            )}
          </>
        )}
      />

      <SectionCard title="Document">
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary, mb: 0.75 }}>
              Type *
            </Typography>
            <Controller
              name="returnable_flag"
              control={control}
              render={({ field }) => (
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  size="small"
                  value={field.value}
                  onChange={(e, v) => v && field.onChange(v)}
                  disabled={isEdit}
                >
                  {RETURNABLE_OPTIONS.map(opt => (
                    <ToggleButton
                      key={opt.value}
                      value={opt.value}
                      sx={{
                        flexDirection: 'column',
                        py: 1,
                        textTransform: 'none',
                        '&.Mui-selected': {
                          backgroundColor: IPD_COLORS.selectedBg,
                          borderColor: IPD_COLORS.primary,
                          color: IPD_COLORS.primary,
                        },
                      }}
                    >
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{opt.label}</Typography>
                      <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary }}>
                        {opt.hint}
                      </Typography>
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              )}
            />
            {isEdit && (
              <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary, mt: 0.5 }}>
                Type cannot be changed after the pass is created.
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} sm={6} md={3.5}>
            <Controller
              name="purpose"
              control={control}
              rules={{ required: 'Required' }}
              render={({ field }) => (
                <InvSelect
                  label="Purpose *"
                  value={field.value}
                  onChange={e => onPurposeChange(e.target.value, field)}
                  options={PURPOSE_OPTIONS}
                  error={errors.purpose}
                />
              )}
            />
          </Grid>

          {/* Sits immediately under Purpose, not down in Remarks: it is part of
              answering "why", and the helper text is there because an empty box
              labelled "purpose" gets filled with "official work". */}
          {showPurposeRemarks && (
            <Grid item xs={12}>
              <Controller
                name="purpose_remarks"
                control={control}
                rules={{ required: 'Required', validate: v => Boolean(v.trim()) || 'Required' }}
                render={({ field }) => (
                  <InvTextField
                    {...field}
                    label={PURPOSE_REMARKS_LABEL}
                    placeholder={PURPOSE_REMARKS_PLACEHOLDER}
                    error={errors.purpose_remarks}
                    multiline
                    rows={2}
                  />
                )}
              />
              
            </Grid>
          )}

          <Grid item xs={12} sm={6} md={3.5}>
            <Controller
              name="gate_pass_datetime"
              control={control}
              rules={{ required: 'Required' }}
              render={({ field }) => (
                <InvTextField
                  {...field}
                  label="Date & time *"
                  type="datetime-local"
                  InputLabelProps={{ shrink: true }}
                  error={errors.gate_pass_datetime}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Controller
              name="store_id"
              control={control}
              rules={{ required: 'Required' }}
              render={({ field }) => (
                <InvSelect
                  label="Store *"
                  value={field.value}
                  onChange={e => changeStore(e, field)}
                  options={storeOptions}
                  error={errors.store_id}
                  // Changing store after a source document is picked would leave
                  // that document's lines on a pass claiming to leave a different
                  // store, so the link is chosen last and pins the store.
                  disabled={lockStore || sourceLinked}
                />
              )}
            />
          </Grid>

          {/* Sits with the store because it is answered together with it: which
              store, and which of that store's documents is this pass carrying. */}
          {sourceType && (
            <Grid item xs={12} sm={6} md={4}>
              <InvSelect
                label={`${GATE_PASS_SOURCE_LABELS[sourceType]} document`}
                value={sourceDocId}
                onChange={e => pickSourceDocument(e.target.value)}
                options={[
                  { value: '', label: 'Not against a document' },
                  ...sourceDocumentOptions(sourceType, sourceDocs, storeId),
                ]}
                disabled={saving || sourceLoading || !storeId}
                helperText={storeId
                  ? 'Picks up that document’s items and quantities — this pass only records the exit.'
                  : 'Select a store first.'}
              />
            </Grid>
          )}

          {returnable && (
            <Grid item xs={12} sm={6} md={4}>
              <Controller
                name="expected_return_date"
                control={control}
                rules={{ required: 'Required' }}
                render={({ field }) => (
                  <InvTextField
                    {...field}
                    label="Expected return date *"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    error={errors.expected_return_date}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Controller
              name="remarks"
              control={control}
              render={({ field }) => <InvTextField {...field} label="Remarks" multiline rows={2} />}
            />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard title="Party & carrier" sx={{ mt: 2.5 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Controller
              name="party_type"
              control={control}
              render={({ field }) => (
                <InvSelect
                  label="Going to *"
                  value={field.value}
                  onChange={field.onChange}
                  options={PARTY_TYPE_OPTIONS}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={5}>
            <Controller
              name="party_name"
              control={control}
              rules={{ required: 'Required' }}
              render={({ field }) => (
                <InvTextField
                  {...field}
                  label="Name *"
                  placeholder="ACME Biomedical Services"
                  error={errors.party_name}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Controller
              name="party_contact"
              control={control}
              render={({ field }) => <InvTextField {...field} label="Contact" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Controller
              name="vehicle_no"
              control={control}
              render={({ field }) => <InvTextField {...field} label="Vehicle no." placeholder="MH12AB1234" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Controller
              name="carrier_name"
              control={control}
              render={({ field }) => <InvTextField {...field} label="Carrier name" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Controller
              name="carrier_contact"
              control={control}
              render={({ field }) => <InvTextField {...field} label="Carrier contact" />}
            />
          </Grid>
        </Grid>
      </SectionCard>

      {showCreditNote && (
        <SectionCard title="Credit note" sx={{ mt: 2.5 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Returning goods to a vendor against a rejected GRN? Raise a <strong>Vendor Return</strong> —
            it handles the stock and the credit. Use this gate pass only to record the physical exit.
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Controller
                name="credit_note_number"
                control={control}
                render={({ field }) => <InvTextField {...field} label="CN number" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Controller
                name="credit_note_date"
                control={control}
                render={({ field }) => (
                  <InvTextField {...field} label="CN date" type="date" InputLabelProps={{ shrink: true }} />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Controller
                name="credit_note_amount"
                control={control}
                render={({ field }) => (
                  <InvTextField {...field} label="CN amount" type="number" inputProps={{ min: 0, step: 0.01 }} />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Controller
                name="credit_note_remarks"
                control={control}
                render={({ field }) => <InvTextField {...field} label="CN remarks" />}
              />
            </Grid>
          </Grid>
        </SectionCard>
      )}

      <SectionCard title="Items" sx={{ mt: 2.5 }}>
        {sourceLinked && (
          <Alert severity="info" sx={{ mb: 2 }}>
            These lines come from the linked {GATE_PASS_SOURCE_LABELS[sourceType]} and cannot be
            edited here — the challan at the barrier has to name the same material as the document
            it accompanies. Clear the document above to enter lines by hand.
          </Alert>
        )}
        {sourceLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} sx={{ color: IPD_COLORS.primary }} />
          </Box>
        ) : (
          <GatePassLineTable
            lines={lines}
            onChange={setLines}
            returnable={returnable}
            storeId={storeId}
            disabled={saving}
            locked={sourceLinked}
          />
        )}
      </SectionCard>
    </Box>
  );
}
